import { Loader } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

import { CommentDto } from 'src/Services/API/generated';

import { CommentItem } from '../CommentItem';
import { CreateComment } from '../CreateComment';

import styles from './CommentsList.module.scss';

export type CommentsListProps = {
  comments: CommentDto[];
  isLoading?: boolean;
  isFetchingMore?: boolean;
  highlightedCommentId?: string;
  onCreateComment: (message: string) => void;
  onEditComment: (commentId: string, message: string) => void;
  onDeleteComment: (commentId: string) => void;
  isCreateLoading?: boolean;
  isEditLoading?: boolean;
  grow?: boolean;
  /** Remplace le placeholder du champ de saisie. */
  createPlaceholder?: string;
  /** Affiche le fil comme des réponses : décalé, avec un filet vertical. */
  threaded?: boolean;
};

const CommentsList = ({
  comments,
  isLoading,
  isFetchingMore,
  highlightedCommentId,
  onCreateComment,
  onEditComment,
  onDeleteComment,
  isCreateLoading,
  isEditLoading,
  grow,
  createPlaceholder,
  threaded,
}: CommentsListProps) => {
  const { t } = useTranslation();

  // When we are in mobile layout, we can not grow because the layout is vertical.
  const canGrow = useMediaQuery('(min-width: 900px)');

  const commentsJSX = comments.map((comment, index, array) => (
    <div key={comment.id}>
      <CommentItem
        comment={comment}
        highlighted={comment.id === highlightedCommentId}
        onEdit={onEditComment}
        onDelete={onDeleteComment}
        isEditLoading={isEditLoading}
      />

      {index !== array.length - 1 ? <div className={styles.separator} /> : null}
    </div>
  ));

  return (
    <div className={styles.container}>
      {commentsJSX.length > 0 || (canGrow && grow) || isLoading ? (
        <>
          <div className={styles.separator} />

          <div
            style={grow ? { flex: '1 1 0' } : { maxHeight: 350 }}
            className={[styles.commentsList, threaded ? styles.threaded : '']
              .filter(Boolean)
              .join(' ')}
          >
            {isLoading && commentsJSX.length === 0 && (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}

            {commentsJSX}

            <div style={{ minHeight: 1, flexShrink: 0 }}>
              {isFetchingMore ? (
                <p className={styles.statusText}>{t('common.loading', 'Chargement en cours...')}</p>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      <div className={styles.separator} style={{ marginBottom: 15 }} />

      <CreateComment
        onSubmit={onCreateComment}
        isLoading={isCreateLoading}
        placeholder={createPlaceholder}
      />
    </div>
  );
};

export default CommentsList;
